'use client'

import React, { useCallback, useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/navigation'
import { useLocale, useTranslations } from 'next-intl'
import { Alert, Box, Button, CircularProgress, Container } from '@mui/material'

import { selectAuth } from '@/src/redux/slices/authSlice'
import { useAppSelector } from '@/src/redux/store'
import { HeaderContainer } from '@/src/components/common/header/HeaderRouter'
import { AccountsSidePanelWrapper } from '@/src/components/desktop/accounts/sidePanel'
import { useAccountTravellers } from '@/src/hooks/travellers/useAccountTravellers'

import { TravellersEmptyState, TravellersView } from './travellers'

const SignInModalWrapper = dynamic(
    () => import(/* webpackChunkName: "signin-modal" */ '../../../common/header/variants/flyin/SignInModal/SignInModalWrapper'),
    { ssr: false }
)

interface DesktopTravellersPageWrapperProps {
    domainCountry: string
}

const DesktopTravellersPageWrapper: React.FC<DesktopTravellersPageWrapperProps> = ({ domainCountry }) => {
    const router = useRouter()
    const locale = useLocale()
    const t = useTranslations('account.travellers')
    const authState = useAppSelector(selectAuth)
    const isLoggedIn = authState.signedIn

    const [isMounted, setIsMounted] = useState(false)
    const [isSignInOpen, setIsSignInOpen] = useState(false)

    const { isLoading, isError, refetch, travellersForView, activeTravellerForView, activeTravellerId, onSelectTraveller } = useAccountTravellers()

    useEffect(() => {
        if (typeof window !== 'undefined') {
            setIsMounted(true)
        }
    }, [])

    useEffect(() => {
        if (isMounted && !isLoggedIn) {
            setIsSignInOpen(true)
        }
    }, [isMounted, isLoggedIn])

    const handleSignInClose = useCallback(() => {
        setIsSignInOpen(false)
        if (!isLoggedIn) {
            router.push(`/${locale}`)
        }
    }, [isLoggedIn, router, locale])

    return (
        <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
            <HeaderContainer domainCountry={domainCountry} />

            <Box sx={{ flex: 1, bgcolor: 'grey.50' }}>
                <Container maxWidth="xl" disableGutters sx={{ py: { xs: 2, md: 3 }, px: { xs: 2, md: 3 } }}>
                    <Box sx={{ display: 'flex', gap: { xs: 2, md: 3 }, alignItems: 'flex-start' }}>
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <AccountsSidePanelWrapper activeIndex={3} />
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            {isLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {isError && !isLoading && (
                                <Alert
                                    severity="error"
                                    sx={{ borderRadius: 2, border: 1, borderColor: 'divider' }}
                                    action={
                                        <Button color="inherit" size="small" onClick={refetch} sx={{ textTransform: 'none', fontWeight: 600 }}>
                                            {t('retry')}
                                        </Button>
                                    }
                                >
                                    {t('failedToLoad')}
                                </Alert>
                            )}

                            {!isLoading && !isError && !travellersForView.length && (
                                <TravellersEmptyState t={t} />
                            )}

                            {!isLoading && !isError && travellersForView.length > 0 && activeTravellerForView && (
                                <TravellersView
                                    t={t}
                                    travellers={travellersForView}
                                    activeTraveller={activeTravellerForView}
                                    activeTravellerId={activeTravellerId}
                                    onSelectTraveller={onSelectTraveller}
                                />
                            )}
                        </Box>
                    </Box>
                </Container>
            </Box>

            {isSignInOpen && <SignInModalWrapper open={isSignInOpen} onClose={handleSignInClose} />}
        </Box>
    )
}

export { DesktopTravellersPageWrapper }
export type { DesktopTravellersPageWrapperProps }
export default DesktopTravellersPageWrapper
